import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../api/client";
import type {
  CatalogListResponse,
  SuiteName,
  SuitesResponse,
} from "../api/types";
import { EmptyState } from "../components/EmptyState";
import { CatalogSkeleton } from "../components/LoadingSkeleton";
import { ProductCard } from "../components/ProductCard";
import { SUITE_LABELS } from "../components/SuiteBadge";

export function CatalogPage(): ReactElement {
  const [suiteFilter, setSuiteFilter] = useState<SuiteName | "">("");
  const [industryFilter, setIndustryFilter] = useState<string>("");
  const [icpFilter, setIcpFilter] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchDebounced, setSearchDebounced] = useState<string>("");

  // Debounce search 200ms
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  const catalogQuery = useQuery<CatalogListResponse>({
    queryKey: ["catalog", suiteFilter, industryFilter, icpFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (suiteFilter) params.set("suite", suiteFilter);
      if (industryFilter) params.set("industry", industryFilter);
      if (icpFilter) params.set("icp", icpFilter);
      const { data } = await apiClient.get<CatalogListResponse>(
        `/api/catalog${params.toString() ? "?" + params.toString() : ""}`,
      );
      return data;
    },
  });

  const suitesQuery = useQuery<SuitesResponse>({
    queryKey: ["suites"],
    queryFn: async () => {
      const { data } = await apiClient.get<SuitesResponse>(
        "/api/catalog/suites",
      );
      return data;
    },
    staleTime: 60 * 60 * 1000, // 1 час — suite данные редко меняются
  });

  const allIndustries = useMemo(() => {
    if (!catalogQuery.data?.items) return [];
    const set = new Set<string>();
    catalogQuery.data.items.forEach((item) =>
      item.industry.forEach((i) => set.add(i)),
    );
    return Array.from(set).sort();
  }, [catalogQuery.data]);

  const filteredItems = useMemo(() => {
    if (!catalogQuery.data?.items) return [];
    if (!searchDebounced.trim()) return catalogQuery.data.items;
    const query = searchDebounced.toLowerCase();
    return catalogQuery.data.items.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.tagline.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.tags.some((t) => t.toLowerCase().includes(query)),
    );
  }, [catalogQuery.data, searchDebounced]);

  const isLoading = catalogQuery.isLoading || suitesQuery.isLoading;
  const isFiltered =
    !!suiteFilter || !!industryFilter || !!icpFilter || !!searchDebounced;

  function resetFilters() {
    setSuiteFilter("");
    setIndustryFilter("");
    setIcpFilter("");
    setSearchInput("");
  }

  return (
    <section className="catalog">
      <header className="catalog__header">
        <div>
          <h1>Каталог продуктов</h1>
          <p className="catalog__subtitle">
            {catalogQuery.data?.filtered
              ? `Подобрано для вас — ${filteredItems.length} ${pluralProducts(filteredItems.length)}`
              : `Все ${filteredItems.length} ${pluralProducts(filteredItems.length)} портфеля IT-Navigate`}
          </p>
        </div>
      </header>

      {/* Suite filter pills */}
      {suitesQuery.data && (
        <div className="catalog__suites" role="tablist" aria-label="Suite filter">
          <button
            type="button"
            role="tab"
            aria-selected={!suiteFilter}
            className={
              !suiteFilter ? "suite-pill suite-pill--active" : "suite-pill"
            }
            onClick={() => setSuiteFilter("")}
          >
            Все
            <span className="suite-pill__count">
              {catalogQuery.data?.total ?? "—"}
            </span>
          </button>
          {suitesQuery.data.items.map((s) => (
            <button
              key={s.suite}
              type="button"
              role="tab"
              aria-selected={suiteFilter === s.suite}
              className={
                suiteFilter === s.suite
                  ? "suite-pill suite-pill--active"
                  : "suite-pill"
              }
              onClick={() => setSuiteFilter(s.suite)}
              style={
                suiteFilter === s.suite
                  ? { backgroundColor: s.accent_color, borderColor: s.accent_color, color: "white" }
                  : { borderColor: s.accent_color }
              }
            >
              {SUITE_LABELS[s.suite]}
              <span className="suite-pill__count">{s.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Secondary filters */}
      <div className="catalog__filters">
        <input
          type="search"
          className="catalog__search"
          placeholder="Поиск по названию, описанию, тегам…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Поиск"
        />
        <select
          value={industryFilter}
          onChange={(e) => setIndustryFilter(e.target.value)}
          aria-label="Отрасль"
        >
          <option value="">Все отрасли</option>
          {allIndustries.map((ind) => (
            <option key={ind} value={ind}>
              {ind}
            </option>
          ))}
        </select>
        <select
          value={icpFilter}
          onChange={(e) => setIcpFilter(e.target.value)}
          aria-label="Роль клиента"
        >
          <option value="">Все роли</option>
          <option value="A1">A1 — CEO / Director</option>
          <option value="B1">B1 — CTO / IT-ментор</option>
          <option value="C1">C1 — Менеджер</option>
        </select>
        {isFiltered && (
          <button
            type="button"
            className="btn-secondary btn-small"
            onClick={resetFilters}
          >
            Сбросить
          </button>
        )}
      </div>

      {isLoading && <CatalogSkeleton count={6} />}

      {catalogQuery.error && (
        <EmptyState
          icon="⚠️"
          title="Не удалось загрузить каталог"
          message="Попробуйте обновить страницу или вернуться позже."
          action={
            <button
              className="btn-primary"
              onClick={() => catalogQuery.refetch()}
            >
              Повторить
            </button>
          }
        />
      )}

      {!isLoading && !catalogQuery.error && (
        <>
          {filteredItems.length > 0 ? (
            <div className="catalog__grid">
              {filteredItems.map((entry) => (
                <ProductCard key={entry.slug} entry={entry} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="🔍"
              title="Ничего не найдено"
              message="Попробуйте изменить фильтры или сбросить их."
              action={
                <button className="btn-secondary" onClick={resetFilters}>
                  Сбросить фильтры
                </button>
              }
            />
          )}
        </>
      )}
    </section>
  );
}

function pluralProducts(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "продукт";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100))
    return "продукта";
  return "продуктов";
}
